import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Form, Button, Icon, Menu, Segment, Header, Table, Message, Loader, Divider, Modal, Checkbox } from 'semantic-ui-react';
import apiClient, { api } from '../api/apiClient';
import { formatStatus } from '../utils/requestHelpers';
import { formatTimestamp } from '../utils/timezone.utils';
import PaginationFooter from './PaginationFooter';
import { useTablePreferences } from '../hooks/useTablePreferences';
import {
    canAccessDecommissionApprovalSubTab,
    canAccessDecommissionRequestSubTab,
    canAccessDecommissionSubApprovalSubTab,
    canCompleteDecommission,
    canExecuteDecommission,
    canSubApproveDecommission,
    formatDecommissionSubmitterStatus,
    getDecommissionSubmitterStatusColor,
    getDefaultDecommissionSubTab
} from '../utils/decommissionRoles';
import RejectionModal from './RejectionModal';
import CreationLogsModal from './CreationLogsModal';
import '../azure/components/form/azureForm.css';

const sectionHeaderStyle = {
    color: '#062D4D',
    marginBottom: '1rem',
    fontWeight: 700
};

const outlineDangerButtonStyle = {
    backgroundColor: '#fff',
    color: '#db2828',
    border: '1px solid #db2828'
};

const displayDecommissionValue = (value) => (value && value !== '' ? value : 'N/A');

const getDecommissionStatusTextColor = (status) => {
    if (!status) return '#062D4D';

    const normalized = String(status).toUpperCase();

    if (normalized === 'REJECTED' || normalized === 'FAILED') return '#CE1B4E';
    if (normalized === 'APPROVED') return '#6F9911';
    if (
        normalized.includes('PENDING') ||
        normalized === 'IN_PROGRESS' ||
        normalized === 'DEPROVISIONED_MANUALLY'
    ) {
        return '#35B7F4';
    }

    return '#062D4D';
};

const formatDecommissionActualStatus = (status) => {
    if (!status) return 'Unknown';

    switch (String(status).toUpperCase()) {
        case 'IN_PROGRESS':
        case 'DEPROVISIONED_MANUALLY':
            return 'Decommissioning';
        case 'APPROVED':
            return 'Decommission Successful';
        case 'FAILED':
            return 'Decommission Failed';
        case 'PENDING_SUB_APPROVER':
        case 'PENDING_APPROVER':
            return 'Pending Sub-Approver';
        case 'PENDING_DEVOPS_APPROVAL':
            return 'Pending DevOps Approval';
        case 'REJECTED':
            return 'Rejected';
        default:
            return formatStatus(status);
    }
};

const getDecommissionActualStatusColor = (status) => {
    const normalized = String(status || '').toUpperCase();

    if (normalized === 'APPROVED') return 'positive';
    if (normalized === 'FAILED' || normalized === 'REJECTED') return 'negative';
    if (
        normalized === 'IN_PROGRESS' ||
        normalized === 'DEPROVISIONED_MANUALLY' ||
        normalized === 'PENDING_SUB_APPROVER' ||
        normalized === 'PENDING_APPROVER' ||
        normalized === 'PENDING_DEVOPS_APPROVAL'
    ) {
        return 'warning';
    }
    return '';
};

const DecommissionRequestDetailsModal = ({
    open,
    onClose,
    request,
    formatStatusFn = formatStatus
}) => {
    const [copyRequestIdSuccess, setCopyRequestIdSuccess] = useState(false);
    const [copyCloudNameSuccess, setCopyCloudNameSuccess] = useState(false);

    const copyToClipboard = async (text, setSuccess) => {
        const value = (text ?? '').toString();
        if (!value) return;

        try {
            await navigator.clipboard.writeText(value);
            setSuccess(true);
            setTimeout(() => setSuccess(false), 2000);
        } catch (err) {
            const textArea = document.createElement('textarea');
            textArea.value = value;
            textArea.style.position = 'fixed';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                setSuccess(true);
                setTimeout(() => setSuccess(false), 2000);
            } catch {
                // ignore
            }
            document.body.removeChild(textArea);
        }
    };

    if (!request) {
        return null;
    }

    const requestId = request.requestId || request.id;
    const cloudName = (request.cloudName || '').toString().trim();
    const isRejected = request.status === 'REJECTED';
    const wasSubStageRejection = isRejected && !request.subApprovedBy;
    const wasDevopsStageRejection = isRejected && !!request.subApprovedBy;
    const statusLabel = formatStatusFn(request.status);
    const statusColor = getDecommissionStatusTextColor(request.status);

    const subApprovedBy =
        request.subApprovedBy || (wasSubStageRejection ? request.rejectedBy : '');
    const subApprovedAt =
        request.subApprovedAt ||
        (wasSubStageRejection ? request.rejectedAt : null);
    const subApproverComment =
        request.subApproverComment ||
        (wasSubStageRejection ? request.rejectionReason : '');

    const approvedBy =
        request.approvedBy || (wasDevopsStageRejection ? request.rejectedBy : '');
    const approverAt =
        request.approvedAt ||
        (request.status === 'FAILED' ? request.updatedAt : null) ||
        (wasDevopsStageRejection ? request.rejectedAt : null);
    const approverComment =
        request.approverComment ||
        (wasDevopsStageRejection ? request.rejectionReason : '');

    const showSubApproverComment =
        !!subApproverComment && (wasSubStageRejection || !!request.subApproverComment);

    return (
        <Modal open={open} onClose={onClose} size="large">
            <Modal.Header>Request Details</Modal.Header>
            <Modal.Content>
                <Table definition>
                    <Table.Body>
                        <Table.Row>
                            <Table.Cell colSpan={2} className="bg-gray-50">
                                <strong>General Request Information</strong>
                            </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell width={4}>Request ID</Table.Cell>
                            <Table.Cell>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span>{displayDecommissionValue(requestId)}</span>
                                    {requestId && (
                                        <Icon
                                            name={copyRequestIdSuccess ? 'check' : 'copy'}
                                            link
                                            onClick={() =>
                                                copyToClipboard(requestId, setCopyRequestIdSuccess)
                                            }
                                            style={{
                                                cursor: 'pointer',
                                                color: copyRequestIdSuccess ? '#6F9911' : '#35B7F4',
                                                margin: 0,
                                                padding: 0,
                                                display: 'inline-flex',
                                                alignItems: 'center'
                                            }}
                                            title={copyRequestIdSuccess ? 'Copied!' : 'Copy Request ID'}
                                        />
                                    )}
                                </div>
                            </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>Submitted By</Table.Cell>
                            <Table.Cell>{displayDecommissionValue(request.submittedBy)}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>Submission Time</Table.Cell>
                            <Table.Cell>{formatTimestamp(request.timestamp)}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>Current Status</Table.Cell>
                            <Table.Cell style={{ color: statusColor, fontWeight: 600 }}>
                                {statusLabel}
                            </Table.Cell>
                        </Table.Row>

                        <Table.Row>
                            <Table.Cell colSpan={2} className="bg-gray-50">
                                <strong>Decommission Details</strong>
                            </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>Cloud Name</Table.Cell>
                            <Table.Cell>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span>{displayDecommissionValue(cloudName)}</span>
                                    {cloudName && (
                                        <Icon
                                            name={copyCloudNameSuccess ? 'check' : 'copy'}
                                            link
                                            onClick={() =>
                                                copyToClipboard(cloudName, setCopyCloudNameSuccess)
                                            }
                                            style={{
                                                cursor: 'pointer',
                                                color: copyCloudNameSuccess ? '#6F9911' : '#35B7F4',
                                                margin: 0,
                                                padding: 0,
                                                display: 'inline-flex',
                                                alignItems: 'center'
                                            }}
                                            title={
                                                copyCloudNameSuccess ? 'Copied!' : 'Copy Cloud Name'
                                            }
                                        />
                                    )}
                                </div>
                            </Table.Cell>
                        </Table.Row>

                        <Table.Row>
                            <Table.Cell colSpan={2} className="bg-gray-50">
                                <strong>Approval Details</strong>
                            </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell colSpan={2} className="bg-gray-50" style={{ fontSize: '0.95em' }}>
                                <strong>Sub Approver</strong>
                            </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>
                                {wasSubStageRejection ? 'Sub Approver' : 'Sub Approved By'}
                            </Table.Cell>
                            <Table.Cell>{displayDecommissionValue(subApprovedBy)}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>
                                {wasSubStageRejection ? 'Sub Approver At' : 'Sub Approved At'}
                            </Table.Cell>
                            <Table.Cell>{formatTimestamp(subApprovedAt)}</Table.Cell>
                        </Table.Row>
                        {showSubApproverComment && (
                            <Table.Row>
                                <Table.Cell>Sub Approver Comment</Table.Cell>
                                <Table.Cell>{displayDecommissionValue(subApproverComment)}</Table.Cell>
                            </Table.Row>
                        )}

                        <Table.Row>
                            <Table.Cell colSpan={2} className="bg-gray-50" style={{ fontSize: '0.95em' }}>
                                <strong>Approver (DevOps)</strong>
                            </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>
                                {wasDevopsStageRejection ? 'Rejected By' : 'Approved By'}
                            </Table.Cell>
                            <Table.Cell>{displayDecommissionValue(approvedBy)}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>Approver Comment</Table.Cell>
                            <Table.Cell>{displayDecommissionValue(approverComment)}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>Approver At</Table.Cell>
                            <Table.Cell>{formatTimestamp(approverAt)}</Table.Cell>
                        </Table.Row>
                        {request.provisioningDeprovisionRequestId && (
                            <Table.Row>
                                <Table.Cell>Provisioning Request ID</Table.Cell>
                                <Table.Cell>
                                    {displayDecommissionValue(
                                        request.provisioningDeprovisionRequestId
                                    )}
                                </Table.Cell>
                            </Table.Row>
                        )}
                    </Table.Body>
                </Table>
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={onClose}>Close</Button>
            </Modal.Actions>
        </Modal>
    );
};

const DecommissionHistoryTable = ({
    requests,
    showSubmittedBy = true,
    showSubApprovedBy = false,
    formatStatusFn = formatDecommissionActualStatus,
    getStatusColorFn = getDecommissionActualStatusColor,
    showActions = false,
    onViewDetails,
    selectable = false,
    selectedRequestIds = [],
    onToggleSelectAll,
    onToggleSelect
}) => (
    <div style={{ overflowX: 'auto' }}>
        <Table celled striped>
            <Table.Header>
                <Table.Row>
                    {selectable && (
                        <Table.HeaderCell collapsing>
                            <Checkbox
                                checked={requests.length > 0 && selectedRequestIds.length === requests.length}
                                indeterminate={selectedRequestIds.length > 0 && selectedRequestIds.length < requests.length}
                                onChange={(e, data) => onToggleSelectAll?.(!!data.checked, requests)}
                            />
                        </Table.HeaderCell>
                    )}
                    <Table.HeaderCell>Timestamp</Table.HeaderCell>
                    <Table.HeaderCell>Request ID</Table.HeaderCell>
                    <Table.HeaderCell>Cloud Name</Table.HeaderCell>
                    {showSubmittedBy && <Table.HeaderCell>Submitted By</Table.HeaderCell>}
                    {showSubApprovedBy && <Table.HeaderCell>Sub Approved By</Table.HeaderCell>}
                    <Table.HeaderCell>Current Status</Table.HeaderCell>
                    <Table.HeaderCell>Approved By</Table.HeaderCell>
                    <Table.HeaderCell>Rejected By</Table.HeaderCell>
                    {showActions && <Table.HeaderCell>Actions</Table.HeaderCell>}
                </Table.Row>
            </Table.Header>
            <Table.Body>
                {requests.map((request) => {
                    const isRejected = request.status === 'REJECTED';

                    return (
                        <Table.Row key={request.requestId || request.id}>
                            {selectable && (
                                <Table.Cell collapsing>
                                    <Checkbox
                                        checked={selectedRequestIds.includes(request.requestId || request.id)}
                                        onChange={(e, data) =>
                                            onToggleSelect?.(request, !!data.checked)
                                        }
                                    />
                                </Table.Cell>
                            )}
                            <Table.Cell>{formatTimestamp(request.timestamp)}</Table.Cell>
                            <Table.Cell>{request.requestId || request.id || '—'}</Table.Cell>
                            <Table.Cell>{request.cloudName || '—'}</Table.Cell>
                            {showSubmittedBy && <Table.Cell>{request.submittedBy || '—'}</Table.Cell>}
                            {showSubApprovedBy && (
                                <Table.Cell>{request.subApprovedBy || '—'}</Table.Cell>
                            )}
                            <Table.Cell className={getStatusColorFn(request.status)}>
                                {formatStatusFn(request.status)}
                            </Table.Cell>
                            <Table.Cell>{isRejected ? '—' : (request.approvedBy || '—')}</Table.Cell>
                            <Table.Cell>{isRejected ? (request.rejectedBy || '—') : '—'}</Table.Cell>
                            {showActions && (
                                <Table.Cell>
                                    <Button
                                        size="small"
                                        primary
                                        onClick={() => onViewDetails?.(request)}
                                    >
                                        View Details
                                    </Button>
                                </Table.Cell>
                            )}
                        </Table.Row>
                    );
                })}
            </Table.Body>
        </Table>
    </div>
);

const DecommissionSubmittedRequestsTable = ({
    requests,
    loading,
    error,
    emptyTitle,
    emptyMessage,
    showSubmittedBy = false,
    showSubApprovedBy = false,
    useSubmitterStatusLabels = false,
    showActions = true,
    allowFilterToggle = true,
    selectable = false,
    selectedRequestIds = [],
    onToggleSelectAll,
    onToggleSelect
}) => {
    const [detailsRequest, setDetailsRequest] = useState(null);
    const [requestIdFilter, setRequestIdFilter] = useState('');
    const [cloudNameFilter, setCloudNameFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [submittedByFilter, setSubmittedByFilter] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const { rowsPerPage, activePage, setActivePage } = useTablePreferences();
    const formatStatusFn = useSubmitterStatusLabels
        ? formatDecommissionSubmitterStatus
        : formatDecommissionActualStatus;
    const getStatusColorFn = useSubmitterStatusLabels
        ? getDecommissionSubmitterStatusColor
        : getDecommissionActualStatusColor;
    const statusOptions = useMemo(() => {
        const uniqueStatuses = Array.from(
            new Set((requests || []).map((r) => String(r?.status || '').trim()).filter(Boolean))
        );
        return uniqueStatuses.map((status) => ({
            key: status,
            value: status,
            text: formatStatusFn(status)
        }));
    }, [requests, formatStatusFn]);

    const activeFilterCount = [
        requestIdFilter,
        cloudNameFilter,
        statusFilter,
        submittedByFilter
    ].filter((v) => !!String(v || '').trim()).length;

    const filteredRequests = useMemo(() => {
        const requestIdTerm = requestIdFilter.trim().toLowerCase();
        const cloudNameTerm = cloudNameFilter.trim().toLowerCase();
        const submittedByTerm = submittedByFilter.trim().toLowerCase();
        const statusTerm = statusFilter.trim().toUpperCase();

        return (requests || []).filter((request) => {
            const requestIdValue = String(request?.requestId || request?.id || '').toLowerCase();
            const cloudNameValue = String(request?.cloudName || '').toLowerCase();
            const submittedByValue = String(request?.submittedBy || '').toLowerCase();
            const statusValue = String(request?.status || '').toUpperCase();

            if (requestIdTerm && !requestIdValue.includes(requestIdTerm)) return false;
            if (cloudNameTerm && !cloudNameValue.includes(cloudNameTerm)) return false;
            if (submittedByTerm && !submittedByValue.includes(submittedByTerm)) return false;
            if (statusTerm && statusValue !== statusTerm) return false;
            return true;
        });
    }, [requests, requestIdFilter, cloudNameFilter, submittedByFilter, statusFilter]);

    const totalPages = Math.max(1, Math.ceil((filteredRequests.length || 0) / rowsPerPage));
    const safePage = Math.min(activePage, totalPages);
    const pageStart = (safePage - 1) * rowsPerPage;
    const pageEnd = pageStart + rowsPerPage;
    const pagedRequests = filteredRequests.slice(pageStart, pageEnd);

    useEffect(() => {
        if (activePage !== safePage) {
            setActivePage(safePage);
        }
    }, [activePage, safePage, setActivePage]);
    if (loading) {
        return (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
                <Loader active inline="centered">Loading requests...</Loader>
            </div>
        );
    }

    if (error) {
        return <Message negative>{error}</Message>;
    }

    if (!requests.length) {
        return (
            <Message info>
                <Message.Header>{emptyTitle || 'No submitted requests'}</Message.Header>
                <p>{emptyMessage || 'You have not submitted any decommission requests yet.'}</p>
            </Message>
        );
    }

    return (
        <>
            <Segment style={{ marginBottom: '1rem' }}>
                {allowFilterToggle && (
                    <div style={{ marginBottom: '0.75rem', display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            size="small"
                            basic
                            color={showFilters ? 'blue' : undefined}
                            primary={!showFilters && activeFilterCount > 0}
                            onClick={() => setShowFilters((prev) => !prev)}
                        >
                            <Icon name="filter" />
                            {showFilters ? 'Hide Filters' : 'Show Filters'}
                            {!showFilters && activeFilterCount > 0 && (
                                <span style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginLeft: '8px',
                                    backgroundColor: '#fff',
                                    color: '#2185d0',
                                    fontSize: '11px',
                                    fontWeight: 700,
                                    minWidth: '22px',
                                    height: '22px',
                                    borderRadius: '12px',
                                    padding: '0 7px',
                                    lineHeight: 1
                                }}>
                                    {activeFilterCount}
                                </span>
                            )}
                        </Button>
                    </div>
                )}
                {(!allowFilterToggle || showFilters) && (
                <Form>
                    <Form.Group widths="equal">
                        <Form.Input
                            label="Request ID"
                            placeholder="Filter by request ID"
                            value={requestIdFilter}
                            onChange={(e, { value }) => {
                                setRequestIdFilter(value || '');
                                setActivePage(1);
                            }}
                        />
                        <Form.Input
                            label="Cloud Name"
                            placeholder="Filter by cloud name"
                            value={cloudNameFilter}
                            onChange={(e, { value }) => {
                                setCloudNameFilter(value || '');
                                setActivePage(1);
                            }}
                        />
                        <Form.Select
                            label="Current Status"
                            placeholder="All statuses"
                            clearable
                            options={statusOptions}
                            value={statusFilter || null}
                            onChange={(e, { value }) => {
                                setStatusFilter((value || '').toString());
                                setActivePage(1);
                            }}
                        />
                        <Form.Input
                            label="Submitted By"
                            placeholder="Filter by submitter"
                            value={submittedByFilter}
                            onChange={(e, { value }) => {
                                setSubmittedByFilter(value || '');
                                setActivePage(1);
                            }}
                        />
                    </Form.Group>
                    <Button
                        type="button"
                        size="small"
                        onClick={() => {
                            setRequestIdFilter('');
                            setCloudNameFilter('');
                            setStatusFilter('');
                            setSubmittedByFilter('');
                            setActivePage(1);
                        }}
                    >
                        Clear Filters
                    </Button>
                </Form>
                )}
            </Segment>

            {!filteredRequests.length ? (
                <Message info>
                    <Message.Header>No matching requests</Message.Header>
                    <p>Try adjusting or clearing the filters.</p>
                </Message>
            ) : (
                <>
            <DecommissionHistoryTable
                requests={pagedRequests}
                showSubmittedBy={showSubmittedBy}
                showSubApprovedBy={showSubApprovedBy}
                formatStatusFn={formatStatusFn}
                getStatusColorFn={getStatusColorFn}
                showActions={showActions}
                onViewDetails={setDetailsRequest}
                selectable={selectable}
                selectedRequestIds={selectedRequestIds}
                onToggleSelectAll={onToggleSelectAll}
                onToggleSelect={onToggleSelect}
            />
            <PaginationFooter
                activePage={safePage}
                totalPages={totalPages}
                totalItems={requests.length}
                filteredItems={filteredRequests}
                hasActiveFilters={filteredRequests.length !== requests.length}
                onPageChange={setActivePage}
                pageStart={pageStart}
                pageEnd={pageEnd}
            />
                </>
            )}
            <DecommissionRequestDetailsModal
                open={!!detailsRequest}
                request={detailsRequest}
                onClose={() => setDetailsRequest(null)}
                formatStatusFn={formatStatusFn}
            />
        </>
    );
};

const DecommissionRequestPanel = ({ userEmail, requests, loading, error, onRefresh }) => {
    const [cloudName, setCloudName] = useState('');
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [pendingCloudName, setPendingCloudName] = useState('');

    const handleClear = () => {
        setCloudName('');
        setErrors({});
        setSubmitError(null);
        setShowConfirmModal(false);
        setPendingCloudName('');
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const trimmed = (cloudName || '').toString().trim();
        if (!trimmed) {
            setErrors({ cloudName: 'Cloud name is required' });
            return;
        }

        setErrors({});
        setSubmitError(null);
        setPendingCloudName(trimmed);
        setShowConfirmModal(true);
    };

    const handleConfirmSubmit = async () => {
        if (!pendingCloudName) return;

        setSubmitting(true);
        setSubmitError(null);
        try {
            await api.multiTenancyDecommission.submitRequest({
                cloudName: pendingCloudName,
                submittedBy: userEmail
            });
            setCloudName('');
            setPendingCloudName('');
            setShowConfirmModal(false);
            onRefresh();
        } catch (err) {
            setSubmitError(
                err?.response?.data?.message ||
                err?.message ||
                'Failed to submit decommission request'
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            {submitError && (
                <Message negative onDismiss={() => setSubmitError(null)}>
                    <Message.Header>Submission failed</Message.Header>
                    <p>{submitError}</p>
                </Message>
            )}

            <Form onSubmit={handleSubmit}>
                <Form.Field required>
                    <label>Cloud Name to Decommission</label>
                    <Form.Input
                        type="text"
                        name="cloudName"
                        placeholder="Enter cloud name"
                        value={cloudName}
                        onChange={(e, { value }) => {
                            setCloudName(value);
                            if (errors.cloudName) {
                                setErrors((prev) => ({ ...prev, cloudName: '' }));
                            }
                        }}
                        error={errors.cloudName ? { content: errors.cloudName } : null}
                    />
                </Form.Field>

                <div className="button-container">
                    <Button type="button" onClick={handleClear} disabled={submitting}>
                        Clear
                    </Button>
                    <Button
                        type="submit"
                        primary
                        loading={submitting}
                        disabled={submitting || !(cloudName || '').toString().trim()}
                    >
                        Submit Request
                    </Button>
                </div>
            </Form>

            <Modal
                open={showConfirmModal}
                onClose={() => {
                    if (!submitting) {
                        setShowConfirmModal(false);
                        setPendingCloudName('');
                    }
                }}
                size="small"
                closeOnDimmerClick={!submitting}
            >
                <Modal.Header>Confirm Decommission Request</Modal.Header>
                <Modal.Content>
                    <Message warning>
                        <Message.Header>Please confirm before submitting</Message.Header>
                        <p style={{ marginBottom: 0 }}>
                            You are about to submit a multi-tenancy decommission request. This action cannot be
                            undone from this form after submission.
                        </p>
                    </Message>
                    <div style={{ marginTop: '1rem' }}>
                        <p style={{ margin: 0 }}>
                            <strong>Cloud Name to Decommission:</strong>{' '}
                            <span style={{ color: '#062D4D' }}>{pendingCloudName}</span>
                        </p>
                    </div>
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        onClick={() => {
                            if (!submitting) {
                                setShowConfirmModal(false);
                                setPendingCloudName('');
                            }
                        }}
                        disabled={submitting}
                    >
                        Back to Form
                    </Button>
                    <Button
                        primary
                        onClick={handleConfirmSubmit}
                        loading={submitting}
                        disabled={submitting}
                    >
                        Confirm & Submit
                    </Button>
                </Modal.Actions>
            </Modal>

            <Divider section style={{ marginTop: '2rem', marginBottom: '1.25rem' }} />

            <Header
                as="h3"
                style={{
                    color: '#062D4D',
                    marginBottom: '1rem',
                    fontWeight: 700
                }}
            >
                My Submitted Requests
            </Header>

            <DecommissionSubmittedRequestsTable
                requests={requests}
                loading={loading}
                error={error}
                useSubmitterStatusLabels
            />
        </>
    );
};

const DecommissionApproval = ({
    userEmail,
    userRoles = [],
    onActionComplete,
    approvalMode = 'devops',
    requiredAccessLabel = 'DevOps'
}) => {
    const isSubApprovalMode = approvalMode === 'sub';
    const [pendingRequests, setPendingRequests] = useState([]);
    const [historyRequests, setHistoryRequests] = useState([]);
    const [pendingLoading, setPendingLoading] = useState(true);
    const [historyLoading, setHistoryLoading] = useState(true);
    const [pendingError, setPendingError] = useState(null);
    const [historyError, setHistoryError] = useState(null);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showRejectionModal, setShowRejectionModal] = useState(false);
    const [processingApproveId, setProcessingApproveId] = useState(null);
    const [processingCompleteId, setProcessingCompleteId] = useState(null);
    const [processingRejectId, setProcessingRejectId] = useState(null);
    const [actionMessage, setActionMessage] = useState(null);
    const [showLogsModal, setShowLogsModal] = useState(false);
    const [creationLogs, setCreationLogs] = useState([]);
    const [approveStreamFailed, setApproveStreamFailed] = useState(false);
    const [selectedHistoryIds, setSelectedHistoryIds] = useState([]);
    const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
    const [deleteConfirmMode, setDeleteConfirmMode] = useState(null); // 'selected' | 'all'
    const [deleteInProgress, setDeleteInProgress] = useState(false);

    const canAct = isSubApprovalMode
        ? canSubApproveDecommission(userRoles)
        : canExecuteDecommission(userRoles);
    const canComplete = !isSubApprovalMode && canCompleteDecommission(userRoles);

    const fetchPending = useCallback(async () => {
        if (!canAct) {
            setPendingLoading(false);
            setPendingRequests([]);
            return;
        }
        try {
            setPendingLoading(true);
            const response = isSubApprovalMode
                ? await api.multiTenancyDecommission.getPendingSubApprovalRequests()
                : await api.multiTenancyDecommission.getPendingRequests();
            setPendingRequests(Array.isArray(response?.data?.requests) ? response.data.requests : []);
            setPendingError(null);
        } catch (err) {
            setPendingError(
                err?.response?.data?.message ||
                err?.message ||
                'Failed to load pending decommission requests'
            );
            setPendingRequests([]);
        } finally {
            setPendingLoading(false);
        }
    }, [canAct, isSubApprovalMode]);

    const fetchHistory = useCallback(async () => {
        if (!canAct) {
            setHistoryLoading(false);
            setHistoryRequests([]);
            return;
        }
        try {
            setHistoryLoading(true);
            const response = isSubApprovalMode
                ? await api.multiTenancyDecommission.getSubApprovalHistory()
                : await api.multiTenancyDecommission.getApprovalHistory();
            setHistoryRequests(Array.isArray(response?.data?.requests) ? response.data.requests : []);
            setSelectedHistoryIds([]);
            setHistoryError(null);
        } catch (err) {
            setHistoryError(
                err?.response?.data?.message ||
                err?.message ||
                'Failed to load decommission approval history'
            );
            setHistoryRequests([]);
        } finally {
            setHistoryLoading(false);
        }
    }, [canAct, isSubApprovalMode]);

    const refreshAll = useCallback(async () => {
        await Promise.all([fetchPending(), fetchHistory()]);
    }, [fetchPending, fetchHistory]);

    useEffect(() => {
        refreshAll();
    }, [refreshAll]);

    const handleSubApprove = async (request) => {
        const requestId = request.requestId || request.id;
        if (!requestId) return;

        setProcessingApproveId(requestId);
        setActionMessage(null);
        try {
            await api.multiTenancyDecommission.subApprove({
                requestId,
                approvedBy: userEmail
            });
            await refreshAll();
            onActionComplete?.();
        } catch (err) {
            setActionMessage({
                type: 'negative',
                text:
                    err?.response?.data?.message ||
                    err?.message ||
                    'Failed to sub-approve decommission request'
            });
        } finally {
            setProcessingApproveId(null);
        }
    };

    const handleDevopsApprove = async (request) => {
        const requestId = request.requestId || request.id;
        if (!requestId) return;

        setProcessingApproveId(requestId);
        setActionMessage(null);
        setApproveStreamFailed(false);
        setShowLogsModal(true);
        setCreationLogs([]);

        const apiBase = apiClient.defaults.baseURL || 'https://qa-tenantbridge.parablu.com';
        const heartbeatInterval = setInterval(() => {
            setCreationLogs((prev) => [
                ...prev,
                `Still processing decommission... (${new Date().toLocaleTimeString()})`
            ]);
        }, 60000);

        try {
            const response = await fetch(`${apiBase}/api/multi-tenancy-decommission/approve`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache',
                    Connection: 'keep-alive',
                    'Keep-Alive': 'timeout=600'
                },
                body: JSON.stringify({
                    requestId,
                    approvedBy: userEmail
                }),
                credentials: 'include'
            });

            if (!response.ok) {
                let errMessage = `HTTP error! status: ${response.status}`;
                try {
                    const errBody = await response.json();
                    errMessage = errBody?.message || errMessage;
                } catch {
                    // ignore parse errors
                }
                throw new Error(errMessage);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let streamFailed = false;

            while (true) {
                const { value, done } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (!line.trim()) continue;
                    try {
                        const data = JSON.parse(line);
                        if (data.type === 'log') {
                            setCreationLogs((prev) => [...prev, data.message]);
                        } else if (data.type === 'warning') {
                            const warningLine = (data.message || '').toString();
                            setCreationLogs((prev) => [
                                ...prev,
                                warningLine.startsWith('🔴') ? warningLine : `🔴 ${warningLine}`
                            ]);
                        } else if (data.type === 'error') {
                            streamFailed = true;
                            setApproveStreamFailed(true);
                            const errLine = (data.message || '').toString();
                            setCreationLogs((prev) => [
                                ...prev,
                                errLine.startsWith('❌') ? errLine : `❌ ${errLine}`
                            ]);
                        }
                    } catch {
                        setCreationLogs((prev) => [...prev, line]);
                    }
                }
            }

            setApproveStreamFailed(streamFailed);
            await refreshAll();
            onActionComplete?.();
        } catch (err) {
            setApproveStreamFailed(true);
            setCreationLogs((prev) => [
                ...prev,
                `❌ Error: ${err?.message || 'Failed to approve decommission request'}`
            ]);
        } finally {
            clearInterval(heartbeatInterval);
            setProcessingApproveId(null);
        }
    };

    const handleApprove = (request) => {
        if (isSubApprovalMode) {
            handleSubApprove(request);
        } else {
            handleDevopsApprove(request);
        }
    };

    const handleComplete = async (request) => {
        const requestId = request.requestId || request.id;
        if (!requestId) return;

        setProcessingCompleteId(requestId);
        setActionMessage(null);
        try {
            const response = await api.multiTenancyDecommission.complete({
                requestId,
                completedBy: userEmail
            });
            const apiLogs = response?.data?.logs;
            if (Array.isArray(apiLogs) && apiLogs.length) {
                console.group(`[Decommission Complete] ${request.cloudName}`);
                apiLogs.forEach((line) => console.log(line));
                console.groupEnd();
            }
            await refreshAll();
            onActionComplete?.();
        } catch (err) {
            setActionMessage({
                type: 'negative',
                text:
                    err?.response?.data?.message ||
                    err?.message ||
                    'Failed to complete decommission request'
            });
        } finally {
            setProcessingCompleteId(null);
        }
    };

    const handleRejectSubmit = async (rejectionReason) => {
        const request = selectedRequest;
        const requestId = request?.requestId || request?.id;
        if (!requestId) return;
        setProcessingRejectId(requestId);
        setActionMessage(null);
        try {
            await api.multiTenancyDecommission.reject({
                requestId,
                rejectionReason,
                rejectedBy: userEmail
            });
            setShowRejectionModal(false);
            setSelectedRequest(null);
            setActionMessage({
                type: 'positive',
                text: `Rejected decommission request for "${request.cloudName}".`
            });
            await refreshAll();
            onActionComplete?.();
        } catch (err) {
            setActionMessage({
                type: 'negative',
                text:
                    err?.response?.data?.message ||
                    err?.message ||
                    'Failed to reject decommission request'
            });
        } finally {
            setProcessingRejectId(null);
        }
    };

    const handleToggleSelectAllHistory = (checked, visibleRequests) => {
        const ids = (visibleRequests || [])
            .map((r) => r.requestId || r.id)
            .filter(Boolean);
        setSelectedHistoryIds((prev) => {
            if (checked) {
                return Array.from(new Set([...prev, ...ids]));
            }
            return prev.filter((id) => !ids.includes(id));
        });
    };

    const handleToggleSelectHistory = (request, checked) => {
        const id = request?.requestId || request?.id;
        if (!id) return;
        setSelectedHistoryIds((prev) => {
            if (checked) return Array.from(new Set([...prev, id]));
            return prev.filter((entry) => entry !== id);
        });
    };

    const handleDeleteSelectedHistory = async () => {
        if (!selectedHistoryIds.length) return;
        setDeleteConfirmMode('selected');
        setShowDeleteConfirmModal(true);
    };

    const handleDeleteAllHistory = async () => {
        if (!historyRequests.length) return;
        setDeleteConfirmMode('all');
        setShowDeleteConfirmModal(true);
    };

    const executeDeleteHistory = async () => {
        if (!deleteConfirmMode) return;

        setDeleteInProgress(true);
        setActionMessage(null);
        try {
            const response = deleteConfirmMode === 'selected'
                ? await api.multiTenancyDecommission.deleteSelectedHistory(selectedHistoryIds)
                : await api.multiTenancyDecommission.deleteAllHistory();
            setActionMessage({
                type: 'positive',
                text: response?.data?.message ||
                    (deleteConfirmMode === 'selected'
                        ? 'Selected history records deleted.'
                        : 'All history records deleted.')
            });
            await fetchHistory();
            setShowDeleteConfirmModal(false);
            setDeleteConfirmMode(null);
        } catch (err) {
            setActionMessage({
                type: 'negative',
                text:
                    err?.response?.data?.message ||
                    err?.message ||
                    (deleteConfirmMode === 'selected'
                        ? 'Failed to delete selected history records'
                        : 'Failed to delete all history records')
            });
        } finally {
            setDeleteInProgress(false);
        }
    };

    if (!canAct) {
        return (
            <Message warning>
                <Message.Header>Approval access required</Message.Header>
                <p>Only users with the {requiredAccessLabel} role can use this view.</p>
            </Message>
        );
    }

    const pendingSectionTitle = isSubApprovalMode
        ? 'Pending Sub-Approvals'
        : 'Pending DevOps Approvals';
    const pendingEmptyMessage = isSubApprovalMode
        ? 'Requests awaiting MT Decommission Sub Approver will appear here.'
        : 'Requests sub-approved and awaiting DevOps execution will appear here.';

    return (
        <>
            {actionMessage && (
                <Message
                    positive={actionMessage.type === 'positive'}
                    negative={actionMessage.type === 'negative'}
                    onDismiss={() => setActionMessage(null)}
                >
                    {actionMessage.text}
                </Message>
            )}

            <Header as="h3" style={sectionHeaderStyle}>
                {pendingSectionTitle}
            </Header>

            {pendingLoading ? (
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                    <Loader active inline="centered">Loading pending requests...</Loader>
                </div>
            ) : pendingError ? (
                <Message negative>{pendingError}</Message>
            ) : !pendingRequests.length ? (
                <Segment placeholder style={{ minHeight: '160px' }}>
                    <Header icon>
                        <Icon name="check circle outline" color="blue" />
                        No pending requests
                    </Header>
                    <Segment.Inline>
                        <p style={{ color: '#475569', margin: 0 }}>{pendingEmptyMessage}</p>
                    </Segment.Inline>
                </Segment>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <Table celled striped>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>Timestamp</Table.HeaderCell>
                                <Table.HeaderCell>Request ID</Table.HeaderCell>
                                <Table.HeaderCell>Cloud Name</Table.HeaderCell>
                                <Table.HeaderCell>Submitted By</Table.HeaderCell>
                                {!isSubApprovalMode && (
                                    <Table.HeaderCell>Sub Approved By</Table.HeaderCell>
                                )}
                                <Table.HeaderCell>Current Status</Table.HeaderCell>
                                <Table.HeaderCell>Actions</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {pendingRequests.map((request) => {
                                const requestId = request.requestId || request.id;
                                const isApproving = processingApproveId === requestId;
                                const isCompleting = processingCompleteId === requestId;
                                const isRejecting = processingRejectId === requestId;
                                const awaitingManualCompletion =
                                    request.status === 'DEPROVISIONED_MANUALLY';
                                const isBusy = isApproving || isCompleting || isRejecting;

                                return (
                                    <Table.Row key={requestId}>
                                        <Table.Cell>{formatTimestamp(request.timestamp)}</Table.Cell>
                                        <Table.Cell>{requestId || '—'}</Table.Cell>
                                        <Table.Cell>{request.cloudName || '—'}</Table.Cell>
                                        <Table.Cell>{request.submittedBy || '—'}</Table.Cell>
                                        {!isSubApprovalMode && (
                                            <Table.Cell>{request.subApprovedBy || '—'}</Table.Cell>
                                        )}
                                        <Table.Cell className={getDecommissionActualStatusColor(request.status)}>
                                            {formatDecommissionActualStatus(request.status)}
                                        </Table.Cell>
                                        <Table.Cell>
                                            {awaitingManualCompletion && canComplete ? (
                                                <Button
                                                    primary
                                                    size="small"
                                                    loading={isCompleting}
                                                    disabled={isBusy}
                                                    onClick={() => handleComplete(request)}
                                                >
                                                    Completed
                                                </Button>
                                            ) : awaitingManualCompletion ? null : (
                                                <>
                                                    <Button
                                                        positive
                                                        size="small"
                                                        loading={isApproving}
                                                        disabled={isBusy}
                                                        onClick={() => handleApprove(request)}
                                                    >
                                                        {isSubApprovalMode ? 'Sub Approve' : 'Approve & Execute'}
                                                    </Button>
                                                    <Button
                                                        negative
                                                        size="small"
                                                        style={{ marginLeft: '0.5rem' }}
                                                        loading={isRejecting}
                                                        disabled={isBusy}
                                                        onClick={() => {
                                                            setSelectedRequest(request);
                                                            setShowRejectionModal(true);
                                                        }}
                                                    >
                                                        Reject
                                                    </Button>
                                                </>
                                            )}
                                        </Table.Cell>
                                    </Table.Row>
                                );
                            })}
                        </Table.Body>
                    </Table>
                </div>
            )}

            <Divider section style={{ marginTop: '2rem', marginBottom: '1.25rem' }} />

            <Header
                as="h3"
                style={{
                    ...sectionHeaderStyle,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                }}
            >
                <Icon name="check circle" color="blue" />
                {isSubApprovalMode ? 'Sub Approval History' : 'Approval History'}
            </Header>
            {!isSubApprovalMode && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                    <div />
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {selectedHistoryIds.length > 0 && (
                            <Button
                                icon
                                labelPosition="left"
                                style={outlineDangerButtonStyle}
                                disabled={deleteInProgress}
                                onClick={handleDeleteSelectedHistory}
                            >
                                <Icon name="trash" />
                                Delete Selected ({selectedHistoryIds.length})
                            </Button>
                        )}
                        <Button
                            icon
                            labelPosition="left"
                            style={outlineDangerButtonStyle}
                            disabled={!historyRequests.length || deleteInProgress}
                            onClick={handleDeleteAllHistory}
                        >
                            <Icon name="trash alternate" />
                            Delete All
                        </Button>
                    </div>
                </div>
            )}

            <DecommissionSubmittedRequestsTable
                requests={historyRequests}
                loading={historyLoading}
                error={historyError}
                showSubmittedBy
                showSubApprovedBy
                emptyTitle={
                    isSubApprovalMode ? 'No sub approval history found' : 'No approval history found'
                }
                emptyMessage="Completed decommission requests will appear here."
                selectable={!isSubApprovalMode}
                selectedRequestIds={!isSubApprovalMode ? selectedHistoryIds : []}
                onToggleSelectAll={!isSubApprovalMode ? handleToggleSelectAllHistory : undefined}
                onToggleSelect={!isSubApprovalMode ? handleToggleSelectHistory : undefined}
            />

            <RejectionModal
                open={showRejectionModal}
                onClose={() => {
                    if (!processingRejectId) {
                        setShowRejectionModal(false);
                        setSelectedRequest(null);
                    }
                }}
                onSubmit={handleRejectSubmit}
                isSubmitting={!!processingRejectId}
            />

            <CreationLogsModal
                open={showLogsModal}
                onClose={() => {
                    setShowLogsModal(false);
                    setApproveStreamFailed(false);
                }}
                logs={creationLogs}
                processLabel="Decommissioning"
                operationFailed={approveStreamFailed}
            />

            <Modal
                open={showDeleteConfirmModal}
                onClose={() => {
                    if (!deleteInProgress) {
                        setShowDeleteConfirmModal(false);
                        setDeleteConfirmMode(null);
                    }
                }}
                size="small"
                closeOnDimmerClick={!deleteInProgress}
            >
                <Modal.Header>Confirm Delete</Modal.Header>
                <Modal.Content>
                    <Message warning>
                        <Message.Header>This action cannot be undone</Message.Header>
                        <p style={{ marginBottom: 0 }}>
                            {deleteConfirmMode === 'selected'
                                ? `Are you sure you want to delete ${selectedHistoryIds.length} selected decommission history record(s)?`
                                : 'Are you sure you want to delete all decommission approval history records?'}
                        </p>
                    </Message>
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        onClick={() => {
                            if (!deleteInProgress) {
                                setShowDeleteConfirmModal(false);
                                setDeleteConfirmMode(null);
                            }
                        }}
                        disabled={deleteInProgress}
                    >
                        Cancel
                    </Button>
                    <Button
                        negative
                        onClick={executeDeleteHistory}
                        loading={deleteInProgress}
                        disabled={deleteInProgress}
                    >
                        <Icon name="trash" />
                        Confirm Delete
                    </Button>
                </Modal.Actions>
            </Modal>
        </>
    );
};

const MultiTenancyDecommission = ({ userEmail, userRoles = [] }) => {
    const showRequestSubTab = canAccessDecommissionRequestSubTab(userRoles);
    const showApprovalSubTab = canAccessDecommissionApprovalSubTab(userRoles);
    const showSubApprovalSubTab = canAccessDecommissionSubApprovalSubTab(userRoles);
    const [activeSubTab, setActiveSubTab] = useState(() => getDefaultDecommissionSubTab(userRoles));
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const allowed =
            (activeSubTab === 'request' && showRequestSubTab) ||
            (activeSubTab === 'approval' && showApprovalSubTab) ||
            (activeSubTab === 'subApproval' && showSubApprovalSubTab);
        if (!allowed) {
            setActiveSubTab(getDefaultDecommissionSubTab(userRoles));
        }
    }, [activeSubTab, showApprovalSubTab, showRequestSubTab, showSubApprovalSubTab, userRoles]);

    const fetchMyRequests = useCallback(async () => {
        if (!userEmail || !showRequestSubTab) return;
        try {
            setLoading(true);
            const response = await api.multiTenancyDecommission.getMyRequests();
            setRequests(Array.isArray(response?.data?.requests) ? response.data.requests : []);
            setError(null);
        } catch (err) {
            setError(
                err?.response?.data?.message ||
                err?.message ||
                'Failed to load submitted requests'
            );
            setRequests([]);
        } finally {
            setLoading(false);
        }
    }, [userEmail, showRequestSubTab]);

    useEffect(() => {
        if (activeSubTab === 'request' && showRequestSubTab) {
            fetchMyRequests();
        }
    }, [activeSubTab, fetchMyRequests, showRequestSubTab]);

    const handleApprovalActionComplete = () => {
        fetchMyRequests();
    };

    const subtitle =
        activeSubTab === 'request'
            ? 'Enter the cloud name you want to decommission and submit your request.'
            : activeSubTab === 'subApproval'
                ? 'Sub-approve requests to send them to DevOps for decommission execution.'
                : 'Approve and execute decommission after sub-approval. Use Completed when manual tenant steps apply.';

    return (
        <>
            <div style={{ marginTop: '1rem', marginBottom: '1.25rem' }}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontSize: '1.35rem',
                        fontWeight: 700,
                        color: '#062D4D',
                        marginBottom: '0.5rem'
                    }}
                >
                    <Icon name="power off" color="blue" />
                    <span>Multi Tenancy Decommission</span>
                </div>
                <div style={{ fontWeight: 500, color: '#062D4D' }}>{subtitle}</div>
            </div>

            {(showRequestSubTab || showApprovalSubTab || showSubApprovalSubTab) && (
                <Menu pointing secondary style={{ marginBottom: '1rem' }}>
                    {showRequestSubTab && (
                        <Menu.Item
                            name="Decommission Request"
                            active={activeSubTab === 'request'}
                            onClick={() => setActiveSubTab('request')}
                        />
                    )}
                    {showSubApprovalSubTab && (
                        <Menu.Item
                            name="Decommission Sub Approval"
                            active={activeSubTab === 'subApproval'}
                            onClick={() => setActiveSubTab('subApproval')}
                        />
                    )}
                    {showApprovalSubTab && (
                        <Menu.Item
                            name="Decommission Approval"
                            active={activeSubTab === 'approval'}
                            onClick={() => setActiveSubTab('approval')}
                        />
                    )}
                </Menu>
            )}

            {activeSubTab === 'request' && showRequestSubTab && (
                <DecommissionRequestPanel
                    userEmail={userEmail}
                    requests={requests}
                    loading={loading}
                    error={error}
                    onRefresh={fetchMyRequests}
                />
            )}
            {activeSubTab === 'subApproval' && showSubApprovalSubTab && (
                <DecommissionApproval
                    userEmail={userEmail}
                    userRoles={userRoles}
                    approvalMode="sub"
                    onActionComplete={handleApprovalActionComplete}
                    requiredAccessLabel="MT Decommission Approver"
                />
            )}
            {activeSubTab === 'approval' && showApprovalSubTab && (
                <DecommissionApproval
                    userEmail={userEmail}
                    userRoles={userRoles}
                    approvalMode="devops"
                    onActionComplete={handleApprovalActionComplete}
                    requiredAccessLabel="DevOps"
                />
            )}
        </>
    );
};

export default MultiTenancyDecommission;
